import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { formatDateTime, formatShortDate } from '../lib/format'
import { discoverySpacePath } from '../lib/paths'
import ConfirmDialog from './ConfirmDialog'
import ShareDraftDialog from './ShareDraftDialog'

// The principal's access card, in a space's header on both tabs: her name
// with the link's state, when it was sent, when she last opened it, when it
// stops working, and the three things an admin does about it — Edit draft
// (the email Share sends, in ShareDraftDialog), Share (or Send link again)
// and Revoke access, the last two behind a confirm that names the address.
// Left of those, Open principal view: the space as the principal sees it
// (/discovery/<id>, a new tab), where the admin can act on her behalf.
// `onChanged` hands back the server's space; `shareRequest` is a counter the
// Settings tab bumps to open the share confirm from there (after moving the
// end date).

const CHIP = {
  active: ['chip chip--success', 'Active'],
  revoked: ['chip chip--muted', 'Revoked'],
  expired: ['chip chip--warning', 'Expired'],
  not_shared: ['chip chip--muted', 'Not shared'],
}

export default function SpaceActions({ space, onChanged, shareRequest = 0 }) {
  const [confirm, setConfirm] = useState(null) // 'share' | 'revoke'
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(null)
  const [status, setStatus] = useState(null) // { type, text }

  useEffect(() => {
    if (shareRequest > 0) setConfirm('share')
  }, [shareRequest])

  const access = space.access ?? { status: 'not_shared' }
  const archived = Boolean(space.archived_at)
  const [chipClass, chipText] = archived ? ['chip chip--muted', 'Closed'] : CHIP[access.status]
  const shared = access.status !== 'not_shared'
  const canShare = !archived && access.status !== 'expired'

  async function run(action) {
    setBusy(action)
    setStatus(null)
    try {
      if (action === 'share') {
        onChanged(await api(`/api/v1/spaces/${space.id}/share`, { method: 'POST' }))
        setStatus({ type: 'success', text: `Personal link sent to ${space.principal_email}.` })
      } else {
        onChanged(await api(`/api/v1/spaces/${space.id}/access/revoke`, { method: 'POST' }))
        setStatus({ type: 'success', text: 'Access revoked — the link no longer opens this space.' })
      }
    } catch (err) {
      setStatus({ type: 'error', text: err.detail || `Could not ${action} this space.` })
    } finally {
      setBusy(null)
      setConfirm(null)
    }
  }

  return (
    <div className="access-wrap">
      <div className="access-card">
        <div className="access-card__cell access-card__cell--who">
          <p className="eyebrow-sm">Principal access</p>
          <p className="access-card__name">
            {space.principal_name}
            <span className={chipClass}>{chipText}</span>
          </p>
        </div>
        <div className="access-card__cell">
          <p className="access-card__label">Link sent</p>
          <p className="access-card__value">
            {space.shared_at ? formatShortDate(space.shared_at) : 'Not yet'}
          </p>
        </div>
        <div className="access-card__cell">
          <p className="access-card__label">Last opened</p>
          <p className="access-card__value">
            {access.last_used_at ? formatDateTime(access.last_used_at) : shared ? 'Not yet' : '—'}
          </p>
        </div>
        <div className="access-card__cell">
          <p className="access-card__label">Works until</p>
          <p className="access-card__value">{formatShortDate(space.ends_at)}</p>
        </div>
        <div className="access-card__actions">
          <a
            className="btn btn--secondary btn--sm"
            href={discoverySpacePath(space.id)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open principal view
          </a>
          {access.status === 'active' && !archived && (
            <button
              type="button"
              className="btn btn--secondary btn--sm btn--quiet-danger"
              onClick={() => setConfirm('revoke')}
              disabled={busy !== null}
            >
              Revoke access
            </button>
          )}
          {canShare && (
            <>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => setEditing(true)}
                disabled={busy !== null}
              >
                Edit draft
              </button>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={() => setConfirm('share')}
                disabled={busy !== null}
              >
                {busy === 'share' ? 'Sending…' : shared ? 'Send link again' : 'Share'}
              </button>
            </>
          )}
          {archived && <span className="access-card__note">Access closed while archived</span>}
        </div>
      </div>
      {status && (
        <p
          className={`access-status${status.type === 'error' ? ' access-status--error' : ''}`}
          role={status.type === 'error' ? 'alert' : 'status'}
        >
          {status.text}
        </p>
      )}

      {editing && (
        <ShareDraftDialog
          space={space}
          onClose={() => setEditing(false)}
          onSaved={(updated) => {
            onChanged(updated)
            setEditing(false)
            setStatus({
              type: 'success',
              text: updated.share_custom
                ? 'Draft saved. Share sends it as written.'
                : 'Draft reset — Share sends the default wording.',
            })
          }}
        />
      )}

      {confirm === 'share' && (
        <ConfirmDialog
          label={shared ? 'Send a new link?' : 'Share this space?'}
          onClose={() => setConfirm(null)}
        >
          <h3 className="text-h5" style={{ marginBottom: 'var(--space-xs)' }}>
            {shared ? 'Send a new link?' : `Share this space with ${space.principal_name}?`}
          </h3>
          <p
            className="text-body-sm"
            style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-m)' }}
          >
            {shared
              ? `A fresh link goes to ${space.principal_email}. The previous one stops working the moment this is sent.`
              : `Emails ${space.principal_email} a personal link from Capston, with ${space.share_reply_to || 'you'} on Reply-To. It opens this space with no sign-in and works until ${formatShortDate(space.ends_at)}.`}
          </p>
          <div className="flex justify-end flex-wrap" style={{ gap: 'var(--space-xs)' }}>
            <button type="button" className="btn btn--secondary" onClick={() => setConfirm(null)}>
              Cancel
            </button>
            <button type="button" className="btn btn--dark" onClick={() => run('share')}>
              {shared ? 'Send new link' : 'Share space'}
            </button>
          </div>
        </ConfirmDialog>
      )}

      {confirm === 'revoke' && (
        <ConfirmDialog label="Revoke access?" onClose={() => setConfirm(null)}>
          <h3 className="text-h5" style={{ marginBottom: 'var(--space-xs)' }}>
            Revoke {space.principal_name}&rsquo;s access?
          </h3>
          <p
            className="text-body-sm"
            style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-m)' }}
          >
            Their link stops opening this space immediately. Staff links keep working,
            results are kept, and sharing again restores access with a new link.
          </p>
          <div className="flex justify-end flex-wrap" style={{ gap: 'var(--space-xs)' }}>
            <button type="button" className="btn btn--secondary" onClick={() => setConfirm(null)}>
              Keep access
            </button>
            <button type="button" className="btn btn--dark" onClick={() => run('revoke')}>
              Revoke access
            </button>
          </div>
        </ConfirmDialog>
      )}
    </div>
  )
}
